import React from 'react';
import { motion } from 'framer-motion';
import BookCover from '../components/BookCover';
import BuyButtons from '../components/BuyButtons';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck } = FiIcons;

const chapters = [
  { num: '01', title: 'The Language of Action', theme: 'Communication', color: 'bg-blue-500', summary: 'Men communicate through deeds. This chapter decodes the silent messages embedded in his everyday actions — and why "I love you" often sounds like a home-cooked meal, a fixed car, or a hand held in the dark.' },
  { num: '02', title: 'The Burden of Provision', theme: 'Responsibility', color: 'bg-amber-600', summary: 'Explore the deep-seated drive in a good man to protect and provide, the invisible pressure he carries, and how you can be his greatest source of peace rather than another weight.' },
  { num: '03', title: 'The Need for Respect', theme: 'Foundation', color: 'bg-emerald-600', summary: 'Respect is not submission — it is recognition. This chapter reveals why feeling respected is the cornerstone of a man\'s emotional security and how to give it authentically.' },
  { num: '04', title: 'Vulnerability & Strength', theme: 'Emotional Depth', color: 'bg-rose-600', summary: 'The myth of the "tough man" collapses here. Learn how a good man experiences vulnerability, why he hides it, and the conditions under which he will let you truly in.' },
  { num: '05', title: 'His Purpose & Drive', theme: 'Identity', color: 'bg-purple-600', summary: 'A man\'s sense of purpose is inseparable from his capacity to love. Understand his mission, support his drive, and watch your relationship deepen in ways you never imagined.' },
  { num: '06', title: 'Loyalty & Commitment', theme: 'Devotion', color: 'bg-indigo-600', summary: 'When a good man commits, he commits with his whole self. This chapter explores the profound nature of his loyalty — and what it means when he stays through the hard times.' },
  { num: '07', title: 'Space & Connection', theme: 'Balance', color: 'bg-teal-600', summary: 'The paradox of closeness: he needs space not to flee from you, but to return to you fully. This truth alone has healed thousands of relationships.' },
  { num: '08', title: 'The Art of Being Chosen', theme: 'Partnership', color: 'bg-brand-accent', summary: 'The final and most powerful truth — about what it means to be the woman he chooses every single day, and how to build a love that chooses itself forward.' },
];

const benefits = [
  'Stop second-guessing what he really means',
  'Communicate in ways that actually reach him',
  'Build a deeper emotional connection',
  'Navigate conflict without losing each other',
  'Feel truly seen, and see him truly',
  'Create a love that lasts and grows',
];

const ChaptersPage = () => (
  <div className="pt-28">
    {/* Hero */}
    <section className="py-16 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-brand-accent font-semibold tracking-widest uppercase text-xs">The Book</span>
            <h1 className="text-5xl font-serif font-bold text-brand-dark mt-3 mb-6 leading-tight">
              How to Love a Good Man
            </h1>
            <p className="text-xl text-slate-600 mb-4 leading-relaxed font-light italic">
              "The 8 Essential Truths of the Male Heart"
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              A groundbreaking guide that takes you inside the emotional world of men — not to excuse their behavior, but to understand their nature. Eight chapters. Eight truths. One complete map to the heart of the man you love.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-10">
              {benefits.map((b) => (
                <div key={b} className="flex items-start gap-2">
                  <FiCheck className="text-brand-accent mt-0.5 flex-shrink-0" size={16} />
                  <span className="text-sm text-slate-600">{b}</span>
                </div>
              ))}
            </div>
            <BuyButtons />
          </div>
          <div className="flex justify-center">
            <BookCover size="md" />
          </div>
        </div>
      </div>
    </section>

    {/* Chapter breakdown */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-4xl font-serif font-bold text-brand-dark">Chapter by Chapter</h2>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto">Each chapter is a truth. Each truth is a key. Together, they open the door to the relationship you've always wanted.</p>
        </div>
        <div className="space-y-6">
          {chapters.map((ch, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="flex gap-6 bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-brand-accent/30 hover:shadow-md transition-all duration-300"
            >
              <div className={`${ch.color} text-white w-14 h-14 rounded-2xl flex items-center justify-center font-serif font-bold text-lg flex-shrink-0`}>
                {ch.num}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="font-serif font-bold text-xl text-brand-dark">{ch.title}</h3>
                  <span className="text-xs bg-slate-200 text-slate-600 px-3 py-1 rounded-full">{ch.theme}</span>
                </div>
                <p className="text-slate-600 leading-relaxed">{ch.summary}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-slate-900 text-white text-center">
      <div className="max-w-2xl mx-auto px-6">
        <h2 className="text-4xl font-serif font-bold mb-5">Ready to Read?</h2>
        <p className="text-slate-400 text-lg mb-10">Choose your preferred format and start today.</p>
        <BuyButtons />
      </div>
    </section>
  </div>
);

export default ChaptersPage;